import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IAppPerformanceSurveyController } from "../../../hexagon/adapters/driver/appPerformanceSurveyController";
import { appNotification } from "../../../util/appNotification";
import { AppNotificationType } from "../../../constants/appNotification";
import { IlocalStorageProvider } from "../../../hexagon/adapters/driven/providers/localStorageProvider";
import { IAnalyticsController } from "../../../hexagon/adapters/driver/analyticsController";

export const useStarRating = (
  appPerformanceSurveyController: IAppPerformanceSurveyController,
  localStorageProvider: IlocalStorageProvider,
  supportController: ISupportController,
  analyticsController: IAnalyticsController,
  engagementTrackerProvider: IEngagementTrackerProvider
) => {
  const [isSurveyOpen, setIsSurveyOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1); // 1=rating, 2=comment, 3=additional
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [note, setNote] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [additionalAnswer, setAdditionalAnswer] = useState<string>("");

  // Get email and facility name from Redux
  const email = useSelector((state: any) => state.auth.user?.email);
  const userID = useSelector((state: any) => state.auth.user?.userID);
  const userRole = useSelector((state: any) => state.auth.user?.role); // For role-based cadence
  const facilityId = useSelector((state: any) => state.clinic.userClinic?.id);
  const facilityName = useSelector(
    (state: any) => state.clinic.userClinic?.name
  );

  useEffect(() => {
    const openSurvey = () => {
      // Check eligibility using new engagement tracker
      const eligibility = engagementTrackerProvider.isEligibleForSurvey();

      if (!eligibility.eligible) {
        return false;
      }

      // Get current module context for analytics
      const moduleContext = engagementTrackerProvider.getCurrentModuleContext();

      setIsSurveyOpen(true);

      // Mark that survey was prompted
      localStorageProvider.set("lastSurveyPromptAt", new Date().toISOString());

      // Track survey shown event with enhanced context
      analyticsController.logEvent("Feedback Rating - Shown", {
        email: email,
        selectedFacility: facilityName,
        selectedFacilityId: facilityId,
        cUserId: userID,
        userRole: userRole,
        triggerType: "auto",
        triggerModule: moduleContext?.moduleName || "unknown",
        triggerRoute: moduleContext?.route || "unknown",
        engagementScore: eligibility.engagementScore,
        meaningfulSessions: eligibility.meaningfulSessions,
        uniqueModules: eligibility.uniqueModules,
        workflowCompletions: eligibility.workflowCompletions,
        daysSinceSignup: Math.floor(eligibility.daysSinceSignup),
        daysSinceLastPrompt: eligibility.daysSinceLastPrompt
          ? Math.floor(eligibility.daysSinceLastPrompt)
          : null,
        userType:
          eligibility.daysSinceLastPrompt === null ? "new" : "returning",
      });

      return true;
    };

    window.addEventListener("openSurvey", openSurvey);

    return () => {
      window.removeEventListener("openSurvey", openSurvey);
      if (!isSurveyOpen) {
        resetSurvey();
      }
    };
  }, [email, facilityName, facilityId, userID, userRole]);

  const resetSurvey = () => {
    setIsSurveyOpen(false);
    setCurrentStep(1);
    setSelectedRating(null);
    setNote("");
    setShowSuccessModal(false);
    setAdditionalAnswer("");
  };

  const onRatingClick = (rating: number) => {
    setSelectedRating(rating);
    // Move to step 2 (comment question)
    setCurrentStep(2);
  };

  const onNoteChange = (note: string) => {
    setNote(note);
  };

  const onCommentSubmit = () => {
    // Move to step 3 (additional question)
    // Comment is optional, so no validation needed
    setCurrentStep(3);
  };

  const onFinalSubmit = async () => {
    if (!additionalAnswer) {
      appNotification({
        nTitle: "Please select an answer",
        nType: AppNotificationType.ERROR,
      });
      return;
    }

    if (selectedRating === null) {
      return;
    }

    try {
      setIsLoading(true);
      const { error, success } = await supportController.submitRating({
        userName: email,
        facilityName: facilityName,
        rating: selectedRating,
        comment: note.trim() || undefined,
        additional_answers: [
          {
            question:
              "How would you feel if you could no longer use Circadia Intelligence Platform?",
            answer: additionalAnswer,
          },
        ],
      });

      if (success) {
        const submissionTime = new Date().toISOString();
        const moduleContext =
          engagementTrackerProvider.getCurrentModuleContext();
        const engagementSummary =
          engagementTrackerProvider.getEngagementSummary();

        // Mark that user has submitted a survey and set the time
        localStorageProvider.set("surveySubmitted", "true");
        localStorageProvider.set("surveyTime", submissionTime);

        // Clear the snooze date since they submitted
        localStorageProvider.remove("surveySnoozedUntil");

        // Track survey submission event with enhanced context
        analyticsController.logEvent("Feedback Rating - Submitted", {
          email: email,
          selectedFacility: facilityName,
          selectedFacilityId: facilityId,
          cUserId: userID,
          userRole: userRole,
          rating: selectedRating,
          hasComment: note.trim().length > 0,
          additionalAnswer: additionalAnswer,
          additional_answers: [
            {
              question:
                "How would you feel if you could no longer use Circadia Intelligence Platform?",
              answer: additionalAnswer,
            },
          ],
          submissionTime,
          submittedFromModule: moduleContext?.moduleName || "unknown",
          submittedFromRoute: moduleContext?.route || "unknown",
          engagementScore:
            engagementSummary.meaningfulSessions * 10 +
            engagementSummary.uniqueModulesVisited * 5 +
            engagementSummary.workflowCompletionsCount * 15,
          meaningfulSessions: engagementSummary.meaningfulSessions,
          uniqueModules: engagementSummary.uniqueModulesVisited,
          workflowCompletions: engagementSummary.workflowCompletionsCount,
          milestonesAchieved: engagementSummary.milestonesAchieved,
          positiveEvents: engagementSummary.positiveEventsCount,
          userType: "returning", // Since they just submitted, they're now a returning user
        });

        // Show success modal and auto-close after 3 seconds
        setShowSuccessModal(true);
        setTimeout(() => {
          resetSurvey();
        }, 3000);
      } else {
        console.error("Failed to submit survey:", error);
        appNotification({
          nTitle: "Failed to submit survey",
          nType: AppNotificationType.ERROR,
        });
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error submitting survey:", error);
      appNotification({
        nTitle: "Failed to submit survey",
        nType: AppNotificationType.ERROR,
      });
      setIsLoading(false);
    }
  };

  const onClose = () => {
    // Set snooze for 14 days when user clicks "Not now" (updated from 3 days)
    const fourteenDaysFromNow = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    localStorageProvider.set(
      "surveySnoozedUntil",
      fourteenDaysFromNow.toISOString()
    );

    const moduleContext = engagementTrackerProvider.getCurrentModuleContext();
    const engagementSummary = engagementTrackerProvider.getEngagementSummary();

    // Track "Not now" click event with enhanced context
    analyticsController.logEvent("Feedback Rating - Not Now", {
      email: email,
      selectedFacility: facilityName,
      selectedFacilityId: facilityId,
      cUserId: userID,
      userRole: userRole,
      snoozeUntil: fourteenDaysFromNow.toISOString(),
      snoozeDays: 14,
      dismissedFromModule: moduleContext?.moduleName || "unknown",
      dismissedFromRoute: moduleContext?.route || "unknown",
      engagementScore:
        engagementSummary.meaningfulSessions * 10 +
        engagementSummary.uniqueModulesVisited * 5 +
        engagementSummary.workflowCompletionsCount * 15,
      meaningfulSessions: engagementSummary.meaningfulSessions,
      uniqueModules: engagementSummary.uniqueModulesVisited,
      workflowCompletions: engagementSummary.workflowCompletionsCount,
      userType: localStorageProvider.get("surveySubmitted")
        ? "returning"
        : "new",
    });

    resetSurvey();
  };

  return {
    isSurveyOpen,
    setIsSurveyOpen,
    currentStep,
    selectedRating,
    setSelectedRating,
    note,
    setNote,
    onRatingClick,
    onNoteChange,
    onCommentSubmit,
    onFinalSubmit,
    onClose,
    showSuccessModal,
    additionalAnswer,
    setAdditionalAnswer,
    email,
    facilityName,
    isLoading,
  };
};
